"use client";

import { useCallback, useEffect, useMemo, useState, Suspense } from "react";
import Link from "next/link";

import useSWR from "swr";
import Hero from "@/components/homes/home-6/Hero";
import Features from "@/components/common/Features";
import CategoryProducts from "@/components/common/CategoryProducts";
import CategoriesShowcase from "@/components/common/CategoriesShowcase";
import DetailsPopup from "@/components/shopDetails/DetailsPopup";
import ShopDetailsTab from "@/components/shopDetails/ShopDetailsTab";
import ShopFullwidth from "@/components/shop/ShopFullwidth";
import { usePWA } from "@/utlis/usePWA";
import { PackageSearch } from "lucide-react";
import { useT, getCategoryVariable } from "@/context/I18nContext";

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || "https://emagyl.ro";

const fetcher = async (url) => {
	const res = await fetch(url);

	let data = null;
	try {
		data = await res.json();
	} catch (e) {
		data = null;
	}

	if (!res.ok) {
		const err = new Error("Request failed");
		err.status = res.status;
		err.data = data;
		throw err;
	}

	return data;
};

function LoadingSkeleton() {
	return (
		<div className='container py-5'>
			<div className='animate-pulse'>
				<div className='h-4 bg-gray-300 rounded w-3/4 mb-4'></div>
				<div className='h-4 bg-gray-300 rounded w-1/2 mb-4'></div>
				<div className='h-32 bg-gray-300 rounded mb-4'></div>
			</div>
		</div>
	);
}

export default function PageContent({ params, searchParams, initialData }) {
	const { slug } = params || {};
	const searchQuery = searchParams?.q || null;
	const isPWA = usePWA();
	const { t } = useT();

	const [pageType, setPageType] = useState(initialData?.pageType ?? null);
	const [categorySort, setCategorySort] = useState("newest");

	// slug helpers
	const isMultiLevel = Array.isArray(slug) && slug.length > 1;
	const isSingleLevel = Array.isArray(slug) && slug.length === 1;
	const slugString = Array.isArray(slug) ? slug[slug.length - 1] : slug;
	const slugPath = Array.isArray(slug) ? slug.join("/") : slug;

	useEffect(() => {
		setPageType(initialData?.pageType ?? null);
		setCategorySort("newest");
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [slugPath, searchQuery, initialData]);

	const sortQuerySuffix = categorySort !== "newest" ? `?sort=${categorySort}` : "";

	const categoryFallbackData = initialData?.pageType === "category" ? initialData.categoryData : undefined;
	const productFallbackData = initialData?.pageType === "product" ? initialData.product : undefined;
	const categoriesFallbackData = initialData?.pageType === "homepage" ? initialData.categories : undefined;

	const { data: multiLevelCategoryData, error: multiLevelCategoryError } = useSWR(
		isMultiLevel ? `/api/categories/${slugPath}${sortQuerySuffix}` : null,
		fetcher,
		{
			revalidateOnFocus: false,
			shouldRetryOnError: false,
			fallbackData: categoryFallbackData,
			revalidateOnMount: categoryFallbackData ? false : undefined,
		},
	);

	const {
		data: singleCategoryData,
		error: singleCategoryError,
		isLoading: categoryLoading,
	} = useSWR(
		(isSingleLevel || (!Array.isArray(slug) && slug)) && !isMultiLevel
			? `/api/categories/${slugString}${sortQuerySuffix}`
			: null,
		fetcher,
		{
			revalidateOnFocus: false,
			shouldRetryOnError: false,
			fallbackData: categoryFallbackData,
			revalidateOnMount: categoryFallbackData ? false : undefined,
		},
	);

	const [extraCategoryProducts, setExtraCategoryProducts] = useState([]);
	const [categoryPage, setCategoryPage] = useState(1);
	const [loadingMoreCategory, setLoadingMoreCategory] = useState(false);

	useEffect(() => {
		setExtraCategoryProducts([]);
		setCategoryPage(1);
	}, [slugPath, categorySort]);

	const {
		data: product,
		error: productError,
		isLoading: productLoading,
	} = useSWR(
		(isSingleLevel || (!Array.isArray(slug) && slug)) && !isMultiLevel ? `/api/products/${slugString}` : null,
		fetcher,
		{
			revalidateOnFocus: false,
			shouldRetryOnError: false,
			fallbackData: productFallbackData,
			revalidateOnMount: productFallbackData ? false : undefined,
		},
	);

	const { data: categories, error: categoriesError } = useSWR(
		!slug && !searchQuery ? `/api/categories2` : null,
		fetcher,
		{
			revalidateOnFocus: false,
			shouldRetryOnError: false,
			fallbackData: categoriesFallbackData,
			revalidateOnMount: categoriesFallbackData ? false : undefined,
		},
	);

	const buildSearchUrl = () => {
		if (!searchQuery) return null;

		const qp = new URLSearchParams({ q: searchQuery });

		if (slug) {
			const slugArray = Array.isArray(slug) ? slug : [slug];
			if (slugArray[0]) qp.append("category", slugArray[0]);
			if (slugArray[1]) qp.append("subcategory", slugArray[1]);
			if (slugArray[2]) qp.append("thirdcategory", slugArray[2]);
		}

		return `/api/search?${qp.toString()}`;
	};

	const { data: searchResults, error: searchError } = useSWR(buildSearchUrl(), fetcher, {
		revalidateOnFocus: false,
		shouldRetryOnError: false,
	});

	const [extraSearchProducts, setExtraSearchProducts] = useState([]);
	const [loadingMoreSearch, setLoadingMoreSearch] = useState(false);

	useEffect(() => {
		setExtraSearchProducts([]);
	}, [searchQuery, slugPath]);

	const activeCategoryData = isMultiLevel ? multiLevelCategoryData : singleCategoryData;

	const allCategoryProducts = useMemo(
		() => [...(activeCategoryData?.productsData || []), ...extraCategoryProducts],
		[activeCategoryData, extraCategoryProducts],
	);

	const handleCategoryLoadMore = useCallback(async () => {
		if (loadingMoreCategory || !activeCategoryData) return;
		if (categoryPage >= (activeCategoryData.totalPages || 1)) return;

		setLoadingMoreCategory(true);
		try {
			const nextPage = categoryPage + 1;
			const data = await fetcher(`/api/categories/${slugPath}?sort=${categorySort}&page=${nextPage}`);
			setExtraCategoryProducts((prev) => [...prev, ...(data.productsData || [])]);
			setCategoryPage(nextPage);
		} catch (err) {
			console.error("Error loading more category products:", err);
		} finally {
			setLoadingMoreCategory(false);
		}
	}, [loadingMoreCategory, activeCategoryData, categoryPage, slugPath, categorySort]);

	const allSearchProducts = useMemo(
		() => [...(searchResults?.products || []), ...extraSearchProducts],
		[searchResults, extraSearchProducts],
	);

	const searchLoadedCount = allSearchProducts.length;
	const searchHasMore = searchResults ? searchLoadedCount < (searchResults.total || 0) : false;

	const handleSearchLoadMore = useCallback(async () => {
		if (loadingMoreSearch || !searchResults || !searchHasMore) return;

		setLoadingMoreSearch(true);
		try {
			const qp = new URLSearchParams({
				q: searchQuery,
				limit: searchResults.limit || 50,
				offset: searchLoadedCount,
			});

			if (slug) {
				const slugArray = Array.isArray(slug) ? slug : [slug];
				if (slugArray[0]) qp.append("category", slugArray[0]);
				if (slugArray[1]) qp.append("subcategory", slugArray[1]);
				if (slugArray[2]) qp.append("thirdcategory", slugArray[2]);
			}

			const data = await fetcher(`/api/search?${qp.toString()}`);
			setExtraSearchProducts((prev) => [...prev, ...(data.products || [])]);
		} catch (err) {
			console.error("Error loading more search results:", err);
		} finally {
			setLoadingMoreSearch(false);
		}
	}, [loadingMoreSearch, searchResults, searchHasMore, searchQuery, searchLoadedCount, slug]);

	useEffect(() => {
		if (searchQuery) return setPageType("search");
		if (!slug) return setPageType("homepage");

		if (isMultiLevel) {
			if (multiLevelCategoryData?.productsData) return setPageType("category");
			if (multiLevelCategoryError) return setPageType("notfound");
			return;
		}

		if (categoryLoading || productLoading) return;

		if (singleCategoryData?.productsData) return setPageType("category");

		if (product) return setPageType("product");

		if (singleCategoryError && productError) return setPageType("notfound");
	}, [
		searchQuery,
		slug,
		slugPath,
		isMultiLevel,
		categoryLoading,
		productLoading,
		multiLevelCategoryData,
		multiLevelCategoryError,
		singleCategoryData,
		singleCategoryError,
		product,
		productError,
	]);

	if (categoriesError || searchError) {
		return (
			<div className='container text-center py-5'>
				<h1>Eroare la încărcarea paginii</h1>
				<Link href='/' className='btn btn-primary'>
					Înapoi
				</Link>
			</div>
		);
	}

	if (!pageType) return <LoadingSkeleton />;

	if (pageType === "homepage") {
		if (!categories) return <LoadingSkeleton />;

		const visibleCategories = categories.filter((cat) => cat?.products?.length > 0);

		return (
			<>
				{visibleCategories.map((cat) => (
					<script
						key={`itemlist-${cat.slug}`}
						type='application/ld+json'
						dangerouslySetInnerHTML={{
							__html: JSON.stringify({
								"@context": "https://schema.org",
								"@type": "ItemList",
								name: cat.name,
								url: `${SITE_URL}/${cat.slug}`,
								itemListElement: cat.products.map((p, i) => ({
									"@type": "ListItem",
									position: i + 1,
									url: `${SITE_URL}/${p.url}`,
									name: p.name,
								})),
							}),
						}}
					/>
				))}
				<Hero mainCategories={initialData?.mainCategories} />
				{isPWA && <CategoriesShowcase />}
				<Suspense fallback={<LoadingSkeleton />}>
					{visibleCategories.map((cat) => (
						<CategoryProducts key={cat.slug} category={cat} />
					))}
				</Suspense>
				{!isPWA && (
					<>
						<div className='mt-5'></div>
						<Features />
					</>
				)}
			</>
		);
	}

	// ----- PRODUS -----
	if (pageType === "product") {
		if (!product) return <LoadingSkeleton />;

		const crumbs = [{ name: t("acasa"), href: "/" }];
		if (product.categoryName && product.categorySlug) {
			crumbs.push({ name: product.categoryName, href: `/${product.categorySlug}` });
		}
		if (product.subcategoryName && product.subcategorySlug) {
			crumbs.push({ name: product.subcategoryName, href: `/${product.categorySlug}/${product.subcategorySlug}` });
		}
		if (product.thirdCategoryName && product.thirdCategorySlug) {
			crumbs.push({
				name: product.thirdCategoryName,
				href: `/${product.categorySlug}/${product.subcategorySlug}/${product.thirdCategorySlug}`,
			});
		}

		return (
			<>
                    {!isPWA && <div style={{ marginTop: 20 }}></div>}
				<nav
					aria-label='breadcrumb'
					className={isPWA ? "container-fluid px-0" : "container-full px_15 lg-px_40"}
					style={{ marginTop: 10, marginBottom: 10, fontSize: 14 }}>
					{crumbs.map((c, i) => (
						<span key={c.href}>
							{i > 0 && <span style={{ margin: "0 6px", color: "#999" }}>{">"}</span>}
							<Link href={c.href} style={{ color: "#6b7280" }}>
								{c.name}
							</Link>
						</span>
					))}
					<span style={{ margin: "0 6px", color: "#999" }}>{">"}</span>
					<span style={{ color: "#333" }}>{product.name}</span>
				</nav>
				<Suspense fallback={<LoadingSkeleton />}>
					<DetailsPopup product={product} />
					<ShopDetailsTab product={product} />
				</Suspense>
			</>
		);
	}

	// ----- CATEGORIE -----
	if (pageType === "category") {
		if (!activeCategoryData?.productsData) return <LoadingSkeleton />;

		const slugArr = Array.isArray(slug) ? slug : [slug];
		const localizedCategoryName = activeCategoryData.categoryNames && getCategoryVariable(activeCategoryData.categoryNames);
		const localizedSubcategoryName =
			activeCategoryData.subcategoryNames && getCategoryVariable(activeCategoryData.subcategoryNames);
		const localizedThirdCategoryName =
			activeCategoryData.thirdCategoryNames && getCategoryVariable(activeCategoryData.thirdCategoryNames);

		const currentCategoryName =
			localizedThirdCategoryName ||
			localizedSubcategoryName ||
			localizedCategoryName ||
			activeCategoryData.thirdCategoryName ||
			activeCategoryData.subcategoryName ||
			activeCategoryData.categoryName;

		const catCrumbs = [{ name: t("acasa"), href: "/" }];
		if (activeCategoryData.categoryName)
			catCrumbs.push({ name: localizedCategoryName || activeCategoryData.categoryName, href: `/${slugArr[0]}` });
		if (activeCategoryData.subcategoryName)
			catCrumbs.push({
				name: localizedSubcategoryName || activeCategoryData.subcategoryName,
				href: `/${slugArr[0]}/${slugArr[1]}`,
			});
		if (activeCategoryData.thirdCategoryName)
			catCrumbs.push({
				name: localizedThirdCategoryName || activeCategoryData.thirdCategoryName,
				href: `/${slugArr[0]}/${slugArr[1]}/${slugArr[2]}`,
			});

		const childLinksLabel = slugArr.length < 3 ? t("category_subcategories") : t("category_related");
		const parentCategoryHref = slugArr.length > 1 ? `/${slugArr.slice(0, -1).join("/")}` : "/";

		return (
			<>
				<div className='tf-page-title'>
					<div className='container-full'>
						<div
							style={{
								display: "flex",
								justifyContent: "center",
								gap: 10,
								marginBottom: 10,
							}}>
							<Link
								href={parentCategoryHref}
								style={{
									display: "inline-flex",
									alignItems: "center",
									gap: 6,
									padding: "6px 14px",
									borderRadius: 20,
									border: "1px solid #e5e5e5",
									color: "#333",
									fontSize: 13,
									textDecoration: "none",
								}}>
								‹ {t("produs_inapoi")}
							</Link>
							<Link
								href='/'
								style={{
									display: "inline-flex",
									alignItems: "center",
									gap: 6,
									padding: "6px 14px",
									borderRadius: 20,
									border: "1px solid #e5e5e5",
									color: "#333",
									fontSize: 13,
									textDecoration: "none",
								}}>
								⌂ {t("acasa")}
							</Link>
						</div>
						<nav aria-label='breadcrumb' className='text-center mb_10' style={{ fontSize: 14 }}>
							{catCrumbs.map((c, i) => (
								<span key={c.href}>
									{i > 0 && <span style={{ margin: "0 6px", color: "#999" }}>{">"}</span>}
									{i === catCrumbs.length - 1 ? (
										<span style={{ color: "#333" }}>{c.name}</span>
									) : (
										<Link href={c.href} style={{ color: "#6b7280" }}>
											{c.name}
										</Link>
									)}
								</span>
							))}
						</nav>
						<h1 className='heading text-center'>{currentCategoryName}</h1>
						{activeCategoryData.categoryDesc && (
							<p className='text-center text-2 text_black-2 mt_5'>
								{activeCategoryData.categoryDesc}
							</p>
						)}
					</div>
				</div>
				<Suspense fallback={<LoadingSkeleton />}>
					<ShopFullwidth
						productsData={allCategoryProducts}
						currentPage={categoryPage}
						totalPages={activeCategoryData.totalPages || 1}
						totalCount={activeCategoryData.totalCount || allCategoryProducts.length}
						loadingMore={loadingMoreCategory}
						onLoadMore={handleCategoryLoadMore}
						onFilterChange={({ sort }) => sort && setCategorySort(sort)}
					/>
				</Suspense>
				{initialData?.children?.length > 0 && (
					<div className='container-full px_15 lg-px_40' style={{ padding: "30px 15px 50px" }}>
						<h2 style={{ fontSize: 18, marginBottom: 14 }}>{childLinksLabel}</h2>
						<div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
							{initialData.children.map((child) => (
								<Link
									key={child.slug}
									href={`/${slugPath}/${child.slug}`}
									style={{
										padding: "8px 16px",
										border: "1px solid #e5e5e5",
										borderRadius: 20,
										fontSize: 14,
										color: "#333",
										textDecoration: "none",
									}}>
									{getCategoryVariable(child)}
								</Link>
							))}
						</div>
					</div>
				)}
			</>
		);
	}

	// ----- SEARCH -----
	if (pageType === "search") {
		if (!searchResults) return <LoadingSkeleton />;

		const categoryContext = slug ? (Array.isArray(slug) ? slug : [slug]) : [];

		return (
			<>
				<div className='tf-page-title'>
					<div className='container-full'>
						<div className='heading text-center'>
							{t("search_results_heading")} {searchResults.query}
							{categoryContext.length > 0 && (
								<span className='text-muted'>
									{" "}
									{t("search_results_in")} {categoryContext.join(" > ")}
								</span>
							)}
						</div>
						<p className='text-center text-2 text_black-2 mt_5'>
							{searchResults.total}{" "}
							{searchResults.total === 1 ? t("search_results_product_found") : t("search_results_products_found")}
						</p>
					</div>
				</div>

				{allSearchProducts.length > 0 ? (
					<Suspense fallback={<LoadingSkeleton />}>
						<ShopFullwidth
							productsData={allSearchProducts}
							currentPage={1}
							totalPages={searchHasMore ? 2 : 1}
							totalCount={searchResults.total}
							loadingMore={loadingMoreSearch}
							onLoadMore={handleSearchLoadMore}
						/>
					</Suspense>
				) : (
					<div className='container text-center py-5'>
						<h3>
							{t("search_no_results_for")} {searchResults.query}
						</h3>
						<Link href='/' className='btn btn-primary mt-3'>
							{t("search_back_home")}
						</Link>
					</div>
				)}
			</>
		);
	}

	// ----- NOT FOUND -----
	if (pageType === "notfound") {
		return (
			<div className='container text-center' style={{ padding: "80px 20px" }}>
				<PackageSearch
					size={56}
					strokeWidth={1.5}
					style={{ color: "#adb5bd", marginBottom: 20 }}
				/>
				<p
					style={{
						fontSize: 18,
						fontWeight: 500,
						color: "#495057",
						maxWidth: 420,
						margin: "0 auto 24px",
					}}>
					La acest moment nu există conținut / produse disponibile pe această pagină.
				</p>
				<Link href='/' className='btn btn-primary'>
					Înapoi
				</Link>
			</div>
		);
	}

	// fallback sigur (nu afișa notfound "default")
	return <LoadingSkeleton />;
}
